// https://fluentsite.z22.web.core.windows.net/quick-start
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme
} from "@fluentui/react-components";
import { HashRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { TeamsFxContext } from "context/teams-context";
import Error404 from "./errors/error-404";
import Index from "components/pages/index";
import ReportsWrapper from "./pages/reports/reports-wrapper";
import ReportsIndex from "./pages/reports/reports-index";
import ActionsWrapper from "./pages/actions/actions-wrapper";
import ActionsIndex from "./pages/actions/actions-index";
import UsersActionsIndex from "./pages/actions/users/users-actions-index";
import SharingLinksActionsIndex from "./pages/actions/sharing-links/sharing-links-actions-index";
import GuestUsersReportsIndex from "./pages/reports/guest-users/guest-users-reports-index";
import SharingLinksReportsIndex from "./pages/reports/sharing-links/sharing-links-reports-index";
import GuestUsersInWorkspaceReport from "./pages/reports/guest-users/guest-users-in-workspace-report";
import GuestUsersInTenantReport from "./pages/reports/guest-users/guest-users-in-tenant-report";
import GuestUserReport from "./pages/reports/guest-users/guest-user-report";
import PPAProvider from "context/ppa-context";
import SharingLinksInTenantReport from "./pages/reports/sharing-links/sharing-links-in-tenant-report";
import SharingLinksInWorkspaceReport from "./pages/reports/sharing-links/sharing-links-in-workspace-report";
import SharingLinksInOneDriveReport from "./pages/reports/sharing-links/sharing-links-in-onedrive-report";
import SignupIndex from "./pages/signup/signup-index";
import SignupWrapper from "./pages/signup/signup-wrapper";
import { useEffect, useMemo, useState } from "react";
import Initialising from "components/initialising-ppa/initialising";
import StorageReportsIndex from "./pages/reports/storage/storage-reports-index";
import StorageInWorkspaceReport from "./pages/reports/storage/storage-in-workspace-report";
import StorageInTenantReport from "./pages/reports/storage/storage-in-tenant-report";
import StorageInOneDriveReport from "./pages/reports/storage/storage-in-onedrive-report";
import OrphanedObjectsReportsIndex from "./pages/reports/orphaned-objects/orphaned-objects-reports-index";
import OrphanedUsersInTenantReport from "./pages/reports/orphaned-objects/orphaned-users-in-tenant-report";
import ReportRequestsWrapper from "./pages/report-requests/report-requests-wrapper";
import ReportRequestsIndex from "./pages/report-requests/report-requests-index";
import ActionRequestsWrapper from "./pages/action-requests/action-requests-wrapper";
import ActionRequestsIndex from "./pages/action-requests/action-requests-index";
import OrphanedMailboxesInTenantReport from "./pages/reports/orphaned-objects/orphaned-mailboxes-in-tenant-report";
import RemoveOrphanedUsersAction from "./pages/action-requests/orphaned-objects/remove-orphaned-users-action";
import RemoveGuestUsersAction from "./pages/action-requests/guest-users/remove-guest-users-action";
import RemoveSharingLinksAction from "./pages/action-requests/sharing-links/remove-sharing-links-action";
import L10nProvider, { ILocale } from "context/l10n-context";
import { IInitialLocaleStrings, ILocaleStrings } from "loc/locale.interfaces";
import { AccessToken } from '@azure/identity';
import { GetTokenOptions } from '@azure/identity';
import preL10nLoadStrings from "loc/pre-l10n-load-translations";
import { IPPAAppProps } from "./ppa-app.props";
import SettingsWrapper from "./pages/settings/settings-wrapper";
import SettingsIndex from "./pages/settings/settings-index";
import PermissionSettingsIndex from "./pages/settings/permissions/permission-settings-index";
import ApplicationRolesIndex from "./pages/settings/permissions/application-roles/application-roles-index";
import NewApplicationRole from "./pages/settings/permissions/application-roles/new-application-role";
import EditApplicationRole from "./pages/settings/permissions/application-roles/edit-application-role";
import ApplicationRoleMembership from "./pages/settings/permissions/application-roles/application-role-membership";
import GuestUsersInWorkspacesByOwnerReport from "./pages/reports/guest-users/guest-users-in-workspaces-by-owner";
import ApplicationRolePermissionLevel from "./pages/settings/permissions/application-roles/application-role-permission-level";
import SharingLinksInWorkspacesByOwnerReport from "./pages/reports/sharing-links/sharing-links-in-workspaces-by-owner-report";
import OrphanedWorkspacesInTenantReport from "./pages/reports/orphaned-objects/orphaned-workspaces-in-tenant-report";
import UsageReportsIndex from "./pages/reports/usage-reports/usage-reports-index";
import InactiveWorkspacesInTenantReport from "./pages/reports/usage-reports/inactive-workspaces-in-tenant-report";
import InactiveWorkspacesByOwnerReport from "./pages/reports/usage-reports/inactive-workspaces-by-owner-report";
import SummaryReportsIndex from "./pages/reports/summary-reports/summary-reports-index";
import WorkspaceSummaryInTenantReport from "./pages/reports/summary-reports/workspace-summary-in-tenant-report";
import WorkspaceSummaryByOwnerReport from "./pages/reports/summary-reports/workspace-summary-by-owner-report";
import FileSizeReportsIndex from "./pages/reports/file-size-reports/file-size-reports-index";
import FileSizeInTenantReport from "./pages/reports/file-size-reports/file-size-in-tenant-report";
import M365EntraIDIndex from "./pages/settings/permissions/m365-entra-id/m365-entra-id-index";


/**
 * The main app which handles the initialization and routing
 * of the app.
 */
export default function PPAApp(props: IPPAAppProps) {

  //
  const { loading, theme, themeString, teamsUserCredential, context } = useTeamsUserCredential({
    initiateLoginEndpoint: props.config.initiateLoginEndpoint!,
    clientId: props.config.clientId!,
  });

  //
  const [initialisingPPA, setInitialisingPPA] = useState<boolean>(true);
  const [isL10nLoaded, setIsL10nLoaded] = useState<boolean>(false);

  //
  const [localeApiUrl, setLocaleApiUrl] = useState<string>();
  const [l10nContainerInitialised, setL10nContainerInitialised] = useState<boolean>(false);
  const [initialStrings, setInitialStrings] = useState<IInitialLocaleStrings>(preL10nLoadStrings.en);

  //
  const setPPAInitialising = (initialising: boolean): void => {
    setInitialisingPPA(initialising);
  }

  //
  const setL10nLoaded = (locale: ILocale | null): void => {
    setIsL10nLoaded(true);
  }

  //
  const getL10nAccessToken = async (scopes: string | string[], options?: GetTokenOptions): Promise<AccessToken | null> => {
    if (teamsUserCredential) {
      return await teamsUserCredential.getToken(scopes, options);
    }
    return null;
  }

  //
  useEffect(() => {
    if (!loading && context?.app?.locale) {
      const localeCode: string = context.app.locale;
      //const localeCode: string = 'te-st';
      //const localeCode: string = 'en-GB';
      setLocaleApiUrl(`${props.config.apiUrl}/api/l10n/${localeCode}`);
      const languageCode = localeCode.split(`-`)[0];
      if (preL10nLoadStrings[languageCode]) {
        setInitialStrings(preL10nLoadStrings[languageCode]);
      }
    }
  }, [loading, context?.app?.locale]);

  //
  useEffect(() => {
    if (localeApiUrl && initialStrings) {
      setL10nContainerInitialised(true);
    }
  }, [localeApiUrl, initialStrings]);

  //
  const initialising = useMemo<boolean>(() => {
    return initialisingPPA || !isL10nLoaded;
  }, [initialisingPPA, isL10nLoaded]);

  return (
    <TeamsFxContext.Provider value={{ theme, themeString, teamsUserCredential }}>
      <L10nProvider
        containerInitialised={l10nContainerInitialised}
        getAccessToken={getL10nAccessToken}
        apiUrl={localeApiUrl}
        loaded={setL10nLoaded}
        initialStrings={initialStrings} >
        <PPAProvider
          teamsUserCredential={teamsUserCredential}
          setInitialising={setPPAInitialising}
          config={props.config}>
          <FluentProvider
            theme={
              themeString === "dark"
                ? teamsDarkTheme
                : themeString === "contrast"
                  ? teamsHighContrastTheme
                  : {
                    ...teamsLightTheme,
                    colorNeutralBackground3: "#eeeeee",
                  }
            }
            style={{ height: '100%', background: 'transparent' }}>
            <Router>
              {initialising &&
                <Initialising />}
              {!initialising &&
                <Routes>
                  <Route path="/tab" element={<Navigate to={"/"} />}></Route>
                  <Route path="/" element={<Index />}></Route>
                  <Route path="/signup" element={<SignupWrapper />}>
                    <Route index element={<SignupIndex />} />
                  </Route>
                  <Route path="reports" element={<ReportsWrapper />}>
                    <Route index element={<ReportsIndex />} />
                    <Route path="guest-users">
                      <Route index element={<GuestUsersReportsIndex />} />
                      <Route path="guest-users-tenant/:id?" element={<GuestUsersInTenantReport />} />
                      <Route path="guest-users-workspace/:id?" element={<GuestUsersInWorkspaceReport />} />
                      <Route path="guest-users-workspaces-by-owner/:id?" element={<GuestUsersInWorkspacesByOwnerReport />} />
                      <Route path="guest-users-user/:id?" element={<GuestUserReport />} />
                    </Route>
                    <Route path="sharing-links">
                      <Route index element={<SharingLinksReportsIndex />} />
                      <Route path="sharing-links-tenant/:id?" element={<SharingLinksInTenantReport />} />
                      <Route path="sharing-links-workspace/:id?" element={<SharingLinksInWorkspaceReport />} />
                      <Route path="sharing-links-workspaces-by-owner/:id?" element={<SharingLinksInWorkspacesByOwnerReport />} />
                      <Route path="sharing-links-onedrive/:id?" element={<SharingLinksInOneDriveReport />} />
                    </Route>
                    <Route path="storage">
                      <Route index element={<StorageReportsIndex />} />
                      <Route path="storage-tenant/:id?" element={<StorageInTenantReport />} />
                      <Route path="storage-workspace/:id?" element={<StorageInWorkspaceReport />} />
                      <Route path="storage-onedrive/:id?" element={<StorageInOneDriveReport />} />
                    </Route>
                    <Route path="orphaned-objects">
                      <Route index element={<OrphanedObjectsReportsIndex />} />
                      <Route path="orphaned-users-tenant/:id?" element={<OrphanedUsersInTenantReport />} />
                      <Route path="orphaned-mailboxes-tenant/:id?" element={<OrphanedMailboxesInTenantReport />} />
                      <Route path="orphaned-workspaces-tenant/:id?" element={<OrphanedWorkspacesInTenantReport />} />
                    </Route>
                    <Route path="usage-reports">
                      <Route index element={<UsageReportsIndex />} />
                      <Route path="inactive-workspaces-tenant/:id?" element={<InactiveWorkspacesInTenantReport />} />
                      <Route path="inactive-workspaces-by-owner/:id?" element={<InactiveWorkspacesByOwnerReport />} />
                    </Route>
                    <Route path="summary-reports">
                      <Route index element={<SummaryReportsIndex />} />
                      <Route path="workspace-summary-tenant/:id?" element={<WorkspaceSummaryInTenantReport />} />
                      <Route path="workspace-summary-by-owner/:id?" element={<WorkspaceSummaryByOwnerReport />} />
                    </Route>
                    <Route path="file-size-reports">
                      <Route index element={<FileSizeReportsIndex />} />
                      <Route path="file-size-tenant/:id?" element={<FileSizeInTenantReport />} />
                    </Route>
                  </Route>
                  <Route path="/report-requests" element={<ReportRequestsWrapper />}>
                    <Route index element={<ReportRequestsIndex />} />
                  </Route>
                  <Route path="actions" element={<ActionsWrapper />}>
                    <Route index element={<ActionsIndex />} />
                    <Route path="users" element={<UsersActionsIndex />} />
                    <Route path="sharing-links" element={<SharingLinksActionsIndex />} />
                  </Route>
                  <Route path="/action-requests" element={<ActionRequestsWrapper />}>
                    <Route index element={<ActionRequestsIndex />} />
                    <Route path="remove-orphaned-users/:id" element={<RemoveOrphanedUsersAction />} />
                    <Route path="remove-guest-users/:id" element={<RemoveGuestUsersAction />} />
                    <Route path="remove-sharing-links/:id" element={<RemoveSharingLinksAction />} />
                  </Route>
                  <Route path="settings" element={<SettingsWrapper />}>
                    <Route index element={<SettingsIndex />} />
                    <Route path="permissions">
                      <Route index element={<PermissionSettingsIndex />} />
                      <Route path="application-roles">
                        <Route index element={<ApplicationRolesIndex />} />
                        <Route path="new" element={<NewApplicationRole />} />
                        <Route path=":id" element={<EditApplicationRole />} />
                        <Route path=":id/permission-level" element={<ApplicationRolePermissionLevel />} />
                        <Route path=":id/membership" element={<ApplicationRoleMembership />} />
                      </Route>
                      <Route path="m365-entra-id">
                        <Route index element={<M365EntraIDIndex />} />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="*" element={<Error404 />}></Route>
                </Routes>}
            </Router>
          </FluentProvider>
        </PPAProvider>
      </L10nProvider>
    </TeamsFxContext.Provider>
  );
}